<script lang="ts" setup>
import {
  ConfigSettingsDocument,
  MediaImageFragment,
  TaxonomyTermToccoEventTeachingMethod,
  TaxonomyTermToccoEventTeachingMethodFieldsFragment,
  ToccoCourseFieldsFragment,
  ToccoELearningFieldsFragment,
  ToccoIntroModuleFieldsFragment,
  ToccoModuleFieldsFragment,
  ToccoUser,
  ToccoWebinarFieldsFragment,
} from '~/api/graphql/generated'
import { AuthorProps } from '../sidebar/components/AuthorSidebar.vue'
import { isRegistrationEnabled as checkRegistrationEnabled, getRegistrationUrl } from '~/lib/utils/toccoUtils'

const { t } = useI18n()

const props = defineProps<{
  entity:
    | ToccoWebinarFieldsFragment
    | ToccoCourseFieldsFragment
    | ToccoModuleFieldsFragment
    | ToccoIntroModuleFieldsFragment
    | ToccoELearningFieldsFragment
}>()

const computedEntity = computed(() => props.entity)
const fieldToccoDataJSON = computed(() => JSON.parse(computedEntity.value.fieldToccoData ?? ''))

const logos = computed(
  () =>
    (computedEntity.value.fieldImages
      ?.map((media) => media?.entity as MediaImageFragment)
      .filter(Boolean) as MediaImageFragment[]) ?? [],
)
const totalRegistrations = computed(() => fieldToccoDataJSON.value.paths.registration.value)
const participationMax = computed(() => fieldToccoDataJSON.value.paths.participation_max.value)

const author = computed(() => {
  const user = computedEntity.value.fieldAdministrator?.entity as ToccoUser
  const firstName = user?.fieldFirstName ? `${user.fieldFirstName} ` : ''
  const lastName = user?.fieldLastName ? `${user.fieldLastName} ` : ''
  const fullName = `${firstName}${lastName}`.trim()

  return {
    image: (user?.fieldProfilePicture?.entity as MediaImageFragment) || {
      fieldMediaImage: { url: '/tocco-user-placeholder.png', height: 135, width: 135 },
    },
    name: fullName,
    alt: fullName,
    job: user?.fieldJob,
    email: user?.fieldEmail,
    phone: user?.fieldPhoneNumber,
  }
})

const googleMapsQuery = computed(() => {
  const teachingMethod = computedEntity.value?.fieldTeachingMethod?.entity as TaxonomyTermToccoEventTeachingMethod

  if ('physical' !== teachingMethod.fieldEnvironment) return ''

  return fieldToccoDataJSON.value.paths.city?.value ?? ''
})

const formattedPrice = computed(() => {
  if (!fieldToccoDataJSON.value.paths.price?.value) {
    return ''
  }
  const price = fieldToccoDataJSON.value.paths.price.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, "'")
  return `CHF ${price}`
})

const sidebarLabelValueItems = computed(() => {
  const items: Array<{ label: string; value: string }> = []
  if (fieldToccoDataJSON.value.paths.abbreviation.value) {
    items.push({
      label: t('tocco.detail.sidebarTableLabels.number'),
      value: fieldToccoDataJSON.value.paths.abbreviation.value,
    })
  }
  if (fieldToccoDataJSON.value.paths.times.value) {
    items.push({
      label: t('tocco.detail.sidebarTableLabels.time'),
      value: fieldToccoDataJSON.value.paths.times.value.split('\n').join('<br/>'),
    })
  }
  if (formattedPrice) {
    items.push({
      label: t('tocco.detail.sidebarTableLabels.cost'),
      value: formattedPrice.value,
    })
  }
  if (computedEntity.value.fieldTeachingMethod) {
    const fieldTeachingMethod = computedEntity.value.fieldTeachingMethod
      .entity as TaxonomyTermToccoEventTeachingMethodFieldsFragment

    if (fieldTeachingMethod?.name) {
      items.push({
        label: t('tocco.detail.sidebarTableLabels.teachingMethod'),
        value: fieldTeachingMethod.name,
      })
    }
  }
  return items
})

const isRegistrationEnabled = computed((): boolean => checkRegistrationEnabled(fieldToccoDataJSON.value))

const isOnWaitingList = computed(() => parseInt(totalRegistrations.value) >= parseInt(participationMax.value))

const isLastPlaces = computed(() => parseInt(participationMax.value) - parseInt(totalRegistrations.value) <= 3)

const registrationLink = computed(() =>
  getRegistrationUrl(fieldToccoDataJSON.value, computedEntity.value.id || '', isRegistrationEnabled.value),
)

const registrationButtonLabel = computed(() => {
  if (!isRegistrationEnabled.value) {
    return t('tocco.detail.sidebarRegistrationButton.inactive')
  }
  return t('tocco.detail.sidebarRegistrationButton.active')
})

const registrationButtonInfo = computed(() => {
  if (isOnWaitingList.value) {
    return t('tocco.detail.sidebarRegistrationInfoWaitingList')
  }

  if (isLastPlaces.value) {
    return t('tocco.detail.sidebarRegistrationInfoLastPlaces')
  }

  return null
})

const { data: configToccoSettings } = await useAsyncQuery(ConfigSettingsDocument)

const inhousePageUrl = computed(() => configToccoSettings.value?.configCareumToccoSettings?.inhousePage ?? '')

const showInhouseButton = computed(() => computedEntity.value.__typename === 'ToccoCourse' && !!inhousePageUrl.value)
</script>

<template>
  <SectionSidebar>
    <SidebarSection v-if="logos.length" class="tocco-sidebar-logos">
      <Image v-for="(logo, i) in logos" :key="i" :entity="logo" type="sidebar-logo" />
    </SidebarSection>

    <TextSidebar :title="t('contact')" text="<p>Careum Weiterbildung<br/>Mühlemattstrasse 42<br/>CH-5000 Aarau</p>" />

    <AuthorSidebar :author="author as AuthorProps" />

    <LocationSidebar
      :title="t('tocco.detail.sidebarLocation')"
      :label="fieldToccoDataJSON.paths.city.value"
      :google-maps-query="googleMapsQuery"
    />

    <LabelValueSidebar :items="sidebarLabelValueItems" />

    <div class="registration-button">
      <Button primary slide :title="registrationButtonLabel" :to="registrationLink" :disabled="!isRegistrationEnabled">
        {{ registrationButtonLabel }}
      </Button>
      <div v-if="registrationButtonInfo" class="registration-button-info">
        <Icon name="alert" />
        <span class="text-small">{{ registrationButtonInfo }}</span>
      </div>
      <Button v-if="showInhouseButton" primary slide :to="inhousePageUrl" class="inhouse-button">
        {{ t('tocco.detail.sidebarInhouseTraining') }}
      </Button>
    </div>
  </SectionSidebar>
</template>

<style lang="scss" scoped>
.tocco-sidebar-logos {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;

  :deep(img) {
    width: 100%;
    max-width: 105px;
    height: auto;

    @include breakpoint(tl) {
      max-width: 140px;
    }
  }
}

.registration-button {
  position: relative;

  :deep(.button) {
    width: 100%;
  }

  .registration-button-info {
    position: relative;

    background: var(--c-blue-light-100);
    color: var(--c-blue-100);

    padding: 16px;

    display: flex;
    gap: 10px;
    align-items: center;
    justify-content: center;

    &:before {
      content: '';
      --arrow-size: 10px;

      border-left: var(--arrow-size) solid rgba(0, 0, 0, 0);
      border-right: var(--arrow-size) solid rgba(0, 0, 0, 0);
      border-bottom: var(--arrow-size) solid var(--c-blue-light-100);

      position: absolute;
      top: -10px;
      left: 50%;

      transform: translateX(-50%);
    }
  }

  .inhouse-button {
    margin-top: 8px;

    --color: var(--c-blue-100);
    --color-hover: var(--c-blue-100);
    --color-border: var(--c-blue-light-100);
    --color-border-hover: var(--c-blue-light-100);
    --color-background: var(--c-blue-light-100);
    --color-background-hover: var(--c-blue-light-100);

    :deep(.text) {
      white-space: pre-line;
    }
  }
}
</style>
